import { observer } from "mobx-react";
import { useTranslation } from 'react-i18next';

import {List, Button, Stack} from "@mui/material";
import clsx from "clsx";
import { ReactComponent as Logo } from "@common/images/aigle_logo.svg";
import { ReactComponent as LogoWithTile } from "@common/images/logo_and_title_Green_BG.svg";
import ChungCheong from "@common/images/c_logo2.png";
import { ReactComponent as SideBarListBtn } from "@common/images/SideBarListBtn.svg";
import { ReactComponent as SubSideToggle } from "@common/images/SubSideToggle.svg";
import { SideBarStyle } from "./SideBarStyle";

import { useLocation, useNavigate } from "react-router-dom";
import useNaviCustom from "../../../hooks/useNaviCustom";
import ChildMenuComponent from "./ChildMenuComponent";
import { getMenus, getMenuType, MenuType, StudentMenu } from "./MenuItem";
import MenuItemComponent from "./MenuItemComponent";
import useEmotionStyles from "../../../styles/shared/useEmotionStyles";
import { Box, IconButton, Typography } from "@mui/material";
import { useStores } from "../../../hooks/useStores";
import { Icon } from "@iconify/react";

type Props = {
  id?: string;
  menuType: MenuType;
  sideBarOpen: boolean;
  changSideBarOpen: (open: boolean) => void;
  projectCode?: string;
  projectStates?: any;
}

function SideBar(props: Props) {
  const classes = useEmotionStyles(SideBarStyle);
  const i18next = useTranslation();

  const { menuType, projectCode, projectStates, sideBarOpen, changSideBarOpen } = props;

  const navigate = useNavigate();
  const location = useLocation();

  const { navigateStore, messageStore, authStore } = useStores();

  const naviCustom = useNaviCustom({
    navigateStore, messageStore, navigate, location
  });

  const handleDrawerOpen = () => changSideBarOpen(!sideBarOpen);
  const handleChangePage = (value: string) => {
    if (value) naviCustom.go(value);
  }
  const handleDownloadGuide = async () => {
    try {
      const url = "/AigleGuideVer1.5.pdf";
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch ${url}: ${response.status}`);
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = "AigleGuideVer1.5.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error(error);
    }
  }

  const menus = authStore.loginUser.type === "Admin" ? getMenus(menuType, projectCode) : StudentMenu();

  const { pathname } = useLocation();
  const isMain = MenuType.Main === getMenuType(pathname);

  const goToList = () => {
    const paths = pathname.split("/");
    const path = paths[1];
    if (path) naviCustom.go(path);
  }

  return (
    <div className={classes.root}>
      {menuType === MenuType.Main ?
        <Stack justifyContent="space-between" className={sideBarOpen ? classes.bodyOpen : clsx(classes.bodyOpen, classes.bodyClose)}>
          <Stack>
            <Box className={classes.header}
              onClick={() => handleChangePage("/projects")}>

              {sideBarOpen ?
                <div style={{ height: 55, display: 'flex', alignItems: 'center', paddingLeft: 16 }}>
                  <LogoWithTile />
                </div>
                :
                <div style={{ height: 55, display: 'flex', alignItems: 'center', paddingLeft: 5 }}>
                  <Logo />
                </div>
              }
            </Box>

            <Box className={clsx(classes.control, classes.controlMain)}>
              {isMain
                && (
                  <IconButton className={sideBarOpen ? clsx(classes.iconBtnReset, classes.iconButtonMarginRight) : classes.iconBtnReset}
                    onClick={handleDrawerOpen}
                    disableRipple>
                    <SubSideToggle className={!sideBarOpen ? classes.toggle : undefined} />
                  </IconButton>
                )
              }
            </Box>

            <List>
              {menus.map(menu => {
                return <MenuItemComponent key={`menu-item-${menu.label}`}
                  menu={menu.label}
                  icon={menu.icon}
                  onClick={() => handleChangePage(menu.link)}
                  urlPrefix={menu.urlPrefix}
                  expanded={sideBarOpen}
                  child={menu.children &&
                    <ChildMenuComponent projectCode={props.projectCode}
                      children={menu.children}
                      onClick={handleChangePage}
                      activeStates={projectStates}
                      expanded={sideBarOpen} />
                  } />
              })}
            </List>
          </Stack>
					{sideBarOpen ?
						<Stack style={{width: "100%"}} gap={3} p={1} >
							{authStore.loginUser.type === "Admin" && (
								<Button className={classes.teacherGuideButton} onClick={handleDownloadGuide}>
									<Icon icon="la:chalkboard-teacher" fontSize={30}/>
									이용가이드 (교사용)
								</Button>
							)}
							<img style={{ width: "90%", display: "block", margin: "0 auto" }} src={ChungCheong} alt="ChungCheong" />
						</Stack>
            :
            <Box>
            </Box>
          }
          
        </Stack>

        :

        <Box className={sideBarOpen ? classes.subSideMenuBoxOpen : clsx(classes.subSideMenuBoxOpen, classes.subSideMenuBoxClose)}>
          <Box className={classes.header}
            style={{ background: '#025949' }}
            onClick={goToList}>
            <Box className={clsx(classes.iconBtnReset, classes.iconToggle, classes.iconBtnList)}>
              <SideBarListBtn />
            </Box>
            {sideBarOpen &&
              <Typography className={classes.headerTitle}>
                {i18next.t("목록으로")}
              </Typography>
            }
          </Box>

          <Box className={classes.control}>
            <IconButton className={sideBarOpen ? classes.iconBtnReset : classes.iconBtnReset}
              onClick={handleDrawerOpen}
              disableRipple>
              <SubSideToggle className={!sideBarOpen ? classes.toggle : undefined} />
            </IconButton>
          </Box>

          <List>
            {menus.map(menu => {
              return <MenuItemComponent key={`menu-item-${menu.label}`}
                menu={menu.label}
                icon={menu.icon}
                onClick={() => handleChangePage(menu.link)}
                urlPrefix={menu.urlPrefix}
                expanded={sideBarOpen}
                child={menu.children &&
                  <ChildMenuComponent projectCode={props.projectCode}
                    children={menu.children}
                    onClick={handleChangePage}
                    activeStates={projectStates}
                    expanded={sideBarOpen} />
                } />
            })}
          </List>
        </Box>
      }
    </div>
  );
}

export default observer<typeof SideBar>(SideBar);
